/**
 * SecurityResetModal.tsx
 * Confirmation modal for resetting exam security violations
 */

import { AlertTriangle, RotateCcw } from 'lucide-react';
import { Button } from '@vsat/shared/components';
import { Card } from '@vsat/shared/components';

interface Props {
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  examName?: string;
}

export default function SecurityResetModal({ isOpen, onConfirm, onCancel, examName }: Props) {
  if (!isOpen) return null;

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm'>
      <Card className='w-full max-w-md mx-4 p-6 shadow-xl border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-900/30 backdrop-blur-sm rounded-2xl group hover:shadow-lg transition-all duration-300 hover:ring-2 hover:ring-primary/20'>
        <div className='flex items-start gap-4'>
          <div className='flex-shrink-0 w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center shadow-lg shadow-orange-500/25'>
            <AlertTriangle className='w-5 h-5 text-white' />
          </div>

          <div className='flex-1'>
            <h3 className='text-lg font-semibold text-orange-900 dark:text-orange-100 mb-2'>
              Reset Security State
            </h3>

            <p className='text-orange-800 dark:text-orange-200 text-sm mb-4'>
              Starting {examName ? `"${examName}"` : 'a new exam'} will reset all security
              violations and create a clean exam session.
            </p>

            <div className='bg-orange-100 dark:bg-orange-900/30 rounded-lg p-3 mb-4 border border-orange-200 dark:border-orange-800'>
              <p className='text-xs text-orange-700 dark:text-orange-300'>
                <strong>This will clear:</strong>
              </p>
              <ul className='text-xs text-orange-700 dark:text-orange-300 mt-1 list-disc list-inside'>
                <li>All violation records</li>
                <li>Tab switch history</li>
                <li>Security warnings</li>
                <li>Previous session data</li>
              </ul>
            </div>

            <div className='flex justify-end gap-2'>
              <Button
                variant='outline'
                onClick={onCancel}
                className='border-orange-300 dark:border-orange-600 text-orange-700 dark:text-orange-300 hover:bg-orange-100 dark:hover:bg-orange-900/30 hover:border-primary/30 transition-all duration-300'
              >
                Cancel
              </Button>
              <Button
                onClick={onConfirm}
                className='bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary text-white font-semibold shadow-lg shadow-primary/25 transition-all duration-300'
              >
                <RotateCcw className='w-4 h-4 mr-2' />
                Start Clean Exam
              </Button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
